import { Divider, Flex, Group, Paper, Stack } from '@mantine/core';
import { UseQueryOptions } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { LayoutItem } from 'react-grid-layout';
import { Responsive, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useNavigate, useParams } from 'react-router';
import { useInitiatives } from '../../../../api/initiative-client/initiative-client.hooks';
import type { Initiative } from '../../../../api/initiative-client/initiative-client.type';
import { usePageTemplates } from '../../../../api/pages-client/pages-client.hooks';
import type { Component, DataEntryComponent } from '../../../../api/pages-client/pages-client.type';
import { useTranslation } from 'react-i18next';
import { PageHeaderMessage } from '../../../../components/page-header-message/page-header-message';
import { useDocumentTitle } from '../../../../helpers/general-helpers';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { baseWhite, secondaryBase, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { H2, Text } from '../../../../ui-library/typography/typography';
import { MenuDrivenContainer } from '../../../menu-driven-container/menu-driven-container';
import { AddWidgetModal } from '../add-widget-modal';
import { EditDashboardModal } from '../dashboard-modal';
import { DashboardInfoCard } from '../dashboard-info-card';
import { DashboardWidget } from '../dashboard-widget';
import { reconstructLayoutFromComponents } from '../../dashboards.helpers';
import { DashboardTemplateActionBar } from './dashboard-template-action-bar';
import { DashboardTemplateMappingKeyInput } from './dashboard-template-mapping-key-input';
import {
  convertPageTemplateToComponents,
  extractTemplateObjectKeys,
  validateTemplateObjectMappings,
} from './dashboard-template-helpers';
import { useChildPageCount, useTemplateSaveActions } from './dashboard-template.hooks';
import { type ObjectKeyInfo, ObjectType } from './dashboard-template.type';
import { SaveTemplateConfirmModal } from './save-template-confirm-modal';

export const DashboardTemplate = () => {
  const i18n = useTranslation();
  useDocumentTitle('Dashboard Template - Bloomfilter');
  const { templateId } = useParams();
  const navigate = useNavigate();
  const organization = useGlobalStore((state) => state.organization);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const portfolios = useGlobalStore((state) => state.portfolios).filter((p) => p.organization?.id === organization?.id);
  const teams = useGlobalStore((state) => state.teams);

  const { width, containerRef, mounted, measureWidth } = useContainerWidth();

  const { pageTemplates, query: pageTemplatesQuery } = usePageTemplates(organization?.id || '', portfolio?.id);
  const pageTemplate = useMemo(() => {
    return pageTemplates.find((template) => template.id === templateId);
  }, [pageTemplates, templateId]);

  const { childPageCount } = useChildPageCount(templateId, organization?.id || '', portfolio?.id || '');

  const { initiatives = [] } = useInitiatives(portfolio?.id, {
    enabled: !!portfolio?.id,
  } as UseQueryOptions<Initiative[]>);

  const [objectKeyMappings, setObjectKeyMappings] = useState<Record<string, string>>({});
  const [formMappings, setFormMappings] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [dataEntryComponents, setDataEntryComponents] = useState<DataEntryComponent[]>([]);
  const [currentLayout, setCurrentLayout] = useState<LayoutItem[]>([]);
  const [originalLayout, setOriginalLayout] = useState<LayoutItem[]>([]);

  const {
    isEditingLayout,
    isEditDetailsOpen,
    isAddWidgetOpen,
    isSaveTemplateModalOpen,
    banner,
    templateHasWidgets,
    isUpdatingTemplate,
    setIsEditingLayout,
    setIsEditDetailsOpen,
    setIsAddWidgetOpen,
    setBanner,
    handleStartEditLayout,
    handleSaveLayout,
    handleCancelLayout,
    handleResetLayout,
    handleEditDetails,
    handleAddWidget,
    handleEditWidget,
    handleDeleteWidget,
    handleConfirmSave,
    handleCancelConfirm,
  } = useTemplateSaveActions({
    pageTemplate,
    templateId,
    components,
    setComponents,
    dataEntryComponents,
    setDataEntryComponents,
    currentLayout,
    setCurrentLayout,
    originalLayout,
    setOriginalLayout,
    objectKeyMappings,
  });

  // Re-measure width when the grid becomes visible (after Apply is clicked)
  // biome-ignore lint/correctness/useExhaustiveDependencies: measureWidth is stable
  useEffect(() => {
    if (mounted && components.length > 0) {
      const timeoutId = setTimeout(() => measureWidth(), 0);
      return () => clearTimeout(timeoutId);
    }
  }, [mounted, components.length]);

  const objectKeys = useMemo(() => {
    if (!pageTemplate) return [];
    return extractTemplateObjectKeys(pageTemplate);
  }, [pageTemplate]);

  // Only reset transient edit state when switching to a different template,
  // not on every refetch that returns a fresh object identity.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pageTemplate identity changes on refetch; we intentionally key on id
  useEffect(() => {
    if (pageTemplate) {
      setObjectKeyMappings({});
      setFormMappings({});
      setComponents([]);
      setDataEntryComponents([]);
      setCurrentLayout([]);
      setOriginalLayout([]);
      setIsEditingLayout(false);
    }
  }, [pageTemplate?.id]);

  const objectKeyInfos = useMemo((): ObjectKeyInfo[] => {
    if (!pageTemplate?.config?.components) return [];

    const keyInfoMap = new Map<string, ObjectKeyInfo>();

    pageTemplate.config.components.forEach((component) => {
      component.component_data?.forEach((data) => {
        data.object_ids.forEach((objectKey) => {
          if (!keyInfoMap.has(objectKey)) {
            keyInfoMap.set(objectKey, {
              key: objectKey,
              objectType: data.object_type as ObjectType,
              usedInComponents: [],
            });
          }
          const info = keyInfoMap.get(objectKey)!;
          if (!info.usedInComponents.includes(component.name)) {
            info.usedInComponents.push(component.name);
          }
        });

        if (data.color_map) {
          Object.keys(data.color_map).forEach((colorKey) => {
            if (!keyInfoMap.has(colorKey)) {
              keyInfoMap.set(colorKey, {
                key: colorKey,
                objectType: data.object_type as ObjectType,
                usedInComponents: [],
              });
            }
            const info = keyInfoMap.get(colorKey)!;
            if (!info.usedInComponents.includes(component.name)) {
              info.usedInComponents.push(component.name);
            }
          });
        }
      });
    });

    return Array.from(keyInfoMap.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [pageTemplate]);

  const getOptionsByObjectType = (objectType: ObjectType) => {
    switch (objectType) {
      case ObjectType.Portfolio:
        return portfolios.map((p) => ({ label: p.name, value: p.id }));
      case ObjectType.Project:
        return teams.map((t) => ({ label: t.name, value: t.id }));
      case ObjectType.Board:
      case ObjectType.Sprint:
        return teams.flatMap((t) =>
          t.subprojects.map((board) => ({ label: `${t.name} - ${board.name}`, value: board.id })),
        );
      case ObjectType.Initiative:
        return initiatives?.map((initiative: Initiative) => ({
          value: initiative.id,
          label: initiative.name,
        }));
      default:
        return [];
    }
  };

  const handleMappingChange = (objectKey: string, selectedUuid: string) => {
    setFormMappings((prev) => ({
      ...prev,
      [objectKey]: selectedUuid,
    }));
  };

  useEffect(() => {
    if (!pageTemplate) return;
    const validation = validateTemplateObjectMappings(pageTemplate, formMappings);
    setValidationErrors(validation.missingKeys);
  }, [pageTemplate, formMappings]);

  const handleApply = () => {
    if (!pageTemplate) return;

    const validation = validateTemplateObjectMappings(pageTemplate, formMappings);

    if (!validation.isValid) {
      setValidationErrors(validation.missingKeys);
      return;
    }

    setObjectKeyMappings({ ...formMappings });

    const convertedComponents = convertPageTemplateToComponents(pageTemplate, formMappings, 'template-preview').map(
      (component, index) => ({
        ...component,
        id: component.id || `template-component-${index}`,
      }),
    );

    const nextDataEntryComponents: DataEntryComponent[] = (pageTemplate.config?.data_entry_components || []).map(
      (component, index) => ({
        ...component,
        id: component.id || `template-data-entry-${index}`,
        page: 'template-preview',
        stored_data: component.stored_data || [],
      }),
    );

    // Data entry components aren't rendered in the preview grid, so exclude them from currentLayout.
    const nextLayout = reconstructLayoutFromComponents(convertedComponents, []);

    setComponents(convertedComponents);
    setDataEntryComponents(nextDataEntryComponents);
    setCurrentLayout(nextLayout);
    setOriginalLayout(nextLayout);
    setIsEditingLayout(false);
  };

  const hasChanges = useMemo(() => {
    const formKeys = Object.keys(formMappings);
    const currentKeys = Object.keys(objectKeyMappings);

    if (formKeys.length !== currentKeys.length) return true;

    return formKeys.some((key) => formMappings[key] !== objectKeyMappings[key]);
  }, [formMappings, objectKeyMappings]);

  const allMappingsComplete =
    objectKeys.length > 0 && validationErrors.length === 0 && objectKeys.every((key) => formMappings[key]);

  const shouldDisableApplyButton = !allMappingsComplete || !hasChanges;

  const header = (
    <Flex align="center" gap="md" mb="lg">
      <Group gap={8} style={{ marginTop: '1.5rem', marginLeft: '1rem' }}>
        <Button
          variant="secondary"
          radius="xl"
          size="sm"
          leftSection={<Icon name="arrow_back" size={20} color={baseWhite} />}
          onClick={() => navigate('/application/dashboards')}
        >
          Back
        </Button>
        <Icon name="dashboard_customize" size={36} color={secondaryBase} />
        <H2>{pageTemplate?.name}</H2>
      </Group>
    </Flex>
  );

  const dashboardTemplateNotFound = Boolean(!pageTemplate && templateId && !pageTemplatesQuery.isLoading);

  return (
    <MenuDrivenContainer header={header}>
      {banner && (
        <div style={{ marginTop: '1rem' }}>
          <PageHeaderMessage message={banner.text} color={banner.color} onClose={() => setBanner(null)} />
        </div>
      )}
      {dashboardTemplateNotFound ? (
        <DashboardInfoCard
          title="Dashboard template not found"
          objectName={i18n.t('common.dashboard.template')}
          iconName="dashboard_customize"
        />
      ) : (
        <Stack>
          <Paper withBorder radius="md" p="md" ml="md" mr="md" mt="md">
            <Group justify="space-between" align="flex-start">
              <Text size="medium" weight="bold">
                {`${i18n.t('common.dashboard.template')} Mapping`}
              </Text>
              <Group align="flex-center" justify="end">
                <Group gap="sm">
                  {objectKeyInfos.map((keyInfo) => (
                    <DashboardTemplateMappingKeyInput
                      key={keyInfo.key}
                      keyInfo={keyInfo}
                      options={getOptionsByObjectType(keyInfo.objectType)}
                      value={formMappings[keyInfo.key] || null}
                      onChange={(value) => handleMappingChange(keyInfo.key, value)}
                    />
                  ))}
                </Group>
                <Divider orientation="vertical" />

                <Group gap="xs">
                  <Button
                    variant="primary"
                    size="sm"
                    radius="xl"
                    onClick={handleApply}
                    disabled={shouldDisableApplyButton}
                    style={{
                      border: shouldDisableApplyButton ? '1px solid var(--sky-dark)' : undefined,
                    }}
                    leftSection={
                      <Icon
                        name="check_circle_outline"
                        size={16}
                        color={!shouldDisableApplyButton ? baseWhite : skyDark}
                        style={{ cursor: shouldDisableApplyButton ? 'not-allowed' : 'pointer' }}
                      />
                    }
                  >
                    Apply
                  </Button>
                </Group>
              </Group>
            </Group>
          </Paper>

          {templateHasWidgets && (
            <DashboardTemplateActionBar
              description={pageTemplate?.description || ''}
              isEditingLayout={isEditingLayout}
              onEditDetails={() => setIsEditDetailsOpen(true)}
              onStartEditLayout={handleStartEditLayout}
              onAddWidget={() => setIsAddWidgetOpen(true)}
              onCancelLayout={handleCancelLayout}
              onResetLayout={handleResetLayout}
              onSaveLayout={handleSaveLayout}
            />
          )}

          {!templateHasWidgets ? (
            <DashboardInfoCard
              title="Object mappings incomplete"
              description="Please select all required objects in the mapping section above to see the dashboard template preview."
              objectName={i18n.t('common.dashboard.template')}
              iconName="dashboard_customize"
            />
          ) : (
            mounted && (
              <Responsive
                key={`grid-${isEditingLayout}`}
                className="layout"
                innerRef={containerRef}
                width={width}
                layouts={{ lg: currentLayout }}
                rowHeight={120}
                margin={[20, 40]}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
                cols={{ lg: 12, md: 9, sm: 6, xs: 3 }}
                onLayoutChange={(layout) => {
                  if (isEditingLayout) {
                    setCurrentLayout([...layout]);
                  }
                }}
                dragConfig={{ enabled: isEditingLayout, handle: '.draggable-handle' }}
                resizeConfig={{
                  enabled: isEditingLayout,
                  // grammar-check disable-next-line NE
                  handles: ['se', 'sw', 'ne', 'nw', 'e', 'w', 's'],
                }}
              >
                {components.map((component) => {
                  if (!component.id) return null;
                  return (
                    <div key={component.id}>
                      <DashboardWidget
                        component={component}
                        onEdit={handleEditWidget}
                        onDelete={() => handleDeleteWidget(component.id!)}
                        isEditingLayout={isEditingLayout}
                      />
                    </div>
                  );
                })}
              </Responsive>
            )
          )}
        </Stack>
      )}

      <EditDashboardModal
        opened={isEditDetailsOpen}
        onClose={() => setIsEditDetailsOpen(false)}
        onCreate={() => undefined}
        onEdit={handleEditDetails}
        initialData={{
          name: pageTemplate?.name || '',
          description: pageTemplate?.description || '',
        }}
      />

      <AddWidgetModal opened={isAddWidgetOpen} onClose={() => setIsAddWidgetOpen(false)} onSave={handleAddWidget} />

      <SaveTemplateConfirmModal
        opened={isSaveTemplateModalOpen}
        isPending={isUpdatingTemplate}
        childPageCount={childPageCount}
        onCancel={handleCancelConfirm}
        onConfirm={handleConfirmSave}
      />
    </MenuDrivenContainer>
  );
};
